import { useId, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import type { ActualsResponse, EvidenceNode } from '../../types/strategicKpis';
import { STATUS_LABEL, type Light } from '../../utils/strategicKpiFormat';
import { evidencePanelModel, rowsAvailability, type EvidencePanelModel, type EvidenceSplit } from '../../utils/strategicKpiEvidence';
import { SidePanel } from './shell/SidePanel';

interface Props {
    /** The actuals payload the page already holds: the panel makes no request of its own (E9). */
    data: ActualsResponse;
    node: EvidenceNode;
    onClose: () => void;
    /** «Show the rows»: the page pushes the drill address (`withDrill`) and shows EvidenceDrill. */
    onShowRows: (node: EvidenceNode) => void;
    /** A part of the split (a team, a person, a company) was chosen: the page opens the panel for that node. */
    onOpenNode?: (node: EvidenceNode) => void;
}

/** The kit's status dot per light (`.dot.fav|attn|unfav|none`); the word always stands beside it. */
const DOT: Record<Light, string> = { green: 'fav', yellow: 'attn', red: 'unfav', none: 'none' };

const LABEL_SIZE = { fontSize: 'var(--fs-label)', lineHeight: 'var(--lh-label)' } as const;
const FIGURE = { fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)', fontWeight: 'var(--fw-figure)' } as const;

/**
 * The evidence of ONE number in the side panel (masterplan skpi-redesign, E9; package B2): actual, target
 * to date, achieved, the split into teams or persons, the definition, and «Show the rows». It is the content
 * of `SidePanel`; the page decides when it is mounted (mounted = open).
 *
 * Everything shown comes from the actuals payload through `evidencePanelModel` (the same selectors and
 * formatters as the steered rows and the ranking) — no request, so it opens at once, and the button carries
 * no row count (the count would need the request). The rows are the drill view's business.
 */
export function EvidencePanel({ data, node, onClose, onShowRows, onOpenNode }: Props) {
    const model = useMemo(() => evidencePanelModel(data, node), [data, node]);
    const reasonId = useId();
    const kpi = data.kpis.find(k => k.kpiKey === node.kpiKey);
    const rows = model ? model.rows : kpi ? rowsAvailability(kpi, node) : ({ ok: false, reason: 'This KPI is not in the current view.' } as const);

    const footer = rows.ok ? (
        <button type="button" className="btn secondary" onClick={() => onShowRows(node)} data-testid="skpi-show-rows">
            Show the rows <ArrowRight className="i sm" aria-hidden="true" />
        </button>
    ) : (
        <>
            <p id={reasonId} className="muted w-full" style={LABEL_SIZE}>{rows.reason}</p>
            <button type="button" className="btn secondary" disabled aria-describedby={reasonId} data-testid="skpi-show-rows">Show the rows</button>
        </>
    );

    return (
        <SidePanel
            title={model?.title ?? kpi?.name ?? node.kpiKey}
            sub={model?.sub ?? node.label}
            onClose={onClose}
            contentKey={`${node.kpiKey}|${node.scopeType}|${node.scopeKey}`}
            footer={footer}
            testId="skpi-evidence-panel"
            closeLabel="Close evidence panel"
        >
            {model ? <Body model={model} onOpenNode={onOpenNode} /> : (
                <p className="muted" style={LABEL_SIZE}>
                    {node.label} has no figures for this KPI in the current selection.
                </p>
            )}
        </SidePanel>
    );
}

function Body({ model: m, onOpenNode }: { model: EvidencePanelModel; onOpenNode?: (node: EvidenceNode) => void }) {
    return (
        <>
            <div className="card" style={{ padding: '12px 14px' }}>
                <dl className="grid grid-cols-3 gap-3">
                    <div className="min-w-0">
                        <dt className="muted" style={LABEL_SIZE}>Actual</dt>
                        <dd className="strong num truncate" style={FIGURE} title={m.noDataText ?? m.actualText}>{m.actualText}</dd>
                        {m.noDataText && <dd className="muted" style={LABEL_SIZE}>{m.noDataText}</dd>}
                        {m.plannedText && <dd className="muted" style={LABEL_SIZE}>{m.plannedText}</dd>}
                    </div>
                    <div className="min-w-0">
                        <dt className="muted" style={LABEL_SIZE}>Target to date</dt>
                        <dd className={`${m.hasTarget ? 'strong num' : 'muted'} truncate`} style={m.hasTarget ? FIGURE : LABEL_SIZE} title={m.targetToDateText}>{m.targetToDateText}</dd>
                    </div>
                    <div className="min-w-0">
                        <dt className="muted" style={LABEL_SIZE}>Achieved</dt>
                        <dd className="inline-flex max-w-full items-center gap-1.5" style={FIGURE}>
                            {m.light !== 'none' && <span className={`dot ${DOT[m.light]}`} aria-hidden="true" />}
                            <span className="strong num truncate">{m.achievedText}</span>
                        </dd>
                        {m.statusWord && <dd className="muted" style={LABEL_SIZE}>{m.statusWord}</dd>}
                        {m.achievedNote && <dd className="muted" style={LABEL_SIZE}>{m.achievedNote}</dd>}
                    </div>
                </dl>
            </div>

            {m.partialNote && <p className="muted" style={LABEL_SIZE}>{m.partialNote}</p>}

            {m.split && <Split split={m.split} onOpenNode={onOpenNode} />}

            <dl className="kv">
                <dt>Window</dt>
                <dd>{m.windowText}</dd>
                {m.targetBasisText && <><dt>Judged against</dt><dd>{m.targetBasisText}</dd></>}
                {m.yearTargetText && <><dt>Year target</dt><dd className="num">{m.yearTargetText}</dd></>}
                {m.personsTargetText && <><dt>Σ person targets</dt><dd className="num" title="Stored independently of the group target and may differ">{m.personsTargetText}</dd></>}
                <dt>Prior year</dt>
                <dd className="num" title={m.priorWindowText}>{m.priorText}</dd>
            </dl>

            <section>
                <h3 className="sect" style={{ marginBottom: 6 }}>Definition</h3>
                <p>{m.definition}</p>
            </section>
        </>
    );
}

function Split({ split, onOpenNode }: { split: EvidenceSplit; onOpenNode?: (node: EvidenceNode) => void }) {
    const first = split.kind === 'teams' ? 'Team' : split.kind === 'persons' ? 'Person' : 'Company';
    return (
        <section data-testid="skpi-evidence-split">
            <h3 className="sect" style={{ marginBottom: 6 }}>{split.title}</h3>
            <table className="tbl tbl-fixed compact">
                <colgroup><col /><col style={{ width: 88 }} /><col style={{ width: 92 }} /></colgroup>
                <thead>
                    <tr><th scope="col">{first}</th><th scope="col" className="n">Actual</th><th scope="col" className="n">Achieved</th></tr>
                </thead>
                <tbody>
                    {split.rows.map(r => (
                        <tr key={`${r.node.scopeType}|${r.node.scopeKey}|${r.sub ?? ''}`} data-skpi-split={r.node.scopeKey}>
                            <td>
                                <div className="cellstack">
                                    {onOpenNode
                                        ? <button type="button" className="lnk block truncate text-left" title={r.label} onClick={() => onOpenNode(r.node)}>{r.label}</button>
                                        : <span className="name">{r.label}</span>}
                                    {r.sub && <span className="sub2">{r.sub}</span>}
                                </div>
                            </td>
                            <td className="n">{r.actualText}</td>
                            <td className="n">
                                <span className="inline-flex items-center justify-end gap-1.5">
                                    {r.light !== 'none' && <span className={`dot ${DOT[r.light]}`} aria-hidden="true" />}
                                    <span className="strong">{r.achievedText}</span>
                                    {r.light !== 'none' && <span className="sr-only"> ({STATUS_LABEL[r.light]})</span>}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}
